import { clsx } from "clsx";
import { Loader2, ShieldAlert, ShieldQuestion } from "lucide-react";
import { useManagedFileOpener } from "@/hooks/useManagedFileOpener";
import { isPlainLeftClick } from "@/utils/managedFile";

/**
 * Download link for a ManagedFile that explains itself when there is nothing to link to.
 *
 * Files uploaded by contractors are held back until the antivirus clears them, so the
 * API returns `url: null` with a `scan_status`. Without this component the user just
 * sees a dash and assumes the upload failed — hence one shared place that turns the
 * verdict into a sentence.
 *
 * With `file.id` the click fetches a link signed AT THAT MOMENT: `url` in a payload is
 * a presigned S3 link that expires an hour after the data was fetched, and these pages
 * stay open much longer ("AccessDenied — Request has expired"). Callers that pass no id
 * behave exactly as before.
 */

export type ScanStatus =
  | "not_required"
  | "pending"
  | "clean"
  | "threat"
  | "unsupported"
  | "failed";

export interface ManagedFileRef {
  /** ManagedFile id — with it the link is re-signed at click time (see above). */
  id?: string;
  url: string | null;
  scan_status?: ScanStatus;
  original_filename?: string;
}

interface Notice {
  label: string;
  title: string;
  className: string;
  Icon: typeof ShieldAlert;
  spin?: boolean;
}

const NOTICES: Partial<Record<ScanStatus, Notice>> = {
  pending: {
    label: "Skanowanie antywirusem…",
    title: "Plik od podwykonawcy czeka na wynik skanowania antywirusowego. Odśwież za chwilę.",
    className: "text-gray-500 dark:text-gray-400",
    Icon: Loader2,
    spin: true,
  },
  threat: {
    label: "Plik odrzucony przez antywirusa",
    title: "Skaner wykrył zagrożenie — plik został usunięty. Poproś podwykonawcę o ponowne wysłanie.",
    className: "text-red-600 dark:text-red-400",
    Icon: ShieldAlert,
  },
  unsupported: {
    label: "Skan niemożliwy — plik zablokowany",
    title: "Antywirus nie potrafił sprawdzić tego pliku, więc nie jest udostępniany do pobrania.",
    className: "text-amber-600 dark:text-amber-500",
    Icon: ShieldQuestion,
  },
  failed: {
    label: "Skan nieudany — plik zablokowany",
    title: "Skanowanie antywirusowe nie powiodło się. Zgłoś to administratorowi.",
    className: "text-amber-600 dark:text-amber-500",
    Icon: ShieldQuestion,
  },
};

interface Props {
  file: ManagedFileRef | null | undefined;
  /** Link text. Defaults to "Pobierz". */
  children?: React.ReactNode;
  /** Icon rendered before the link text (not used for scan notices — those carry their own). */
  icon?: React.ReactNode;
  /** Rendered when there is no file at all. Defaults to an em dash. */
  empty?: React.ReactNode;
  className?: string;
  iconSize?: number;
}

export function ManagedFileLink({
  file,
  children = "Pobierz",
  icon,
  empty = <span className="text-xs text-gray-400 dark:text-gray-500">—</span>,
  className,
  iconSize = 12,
}: Props) {
  const { openFile } = useManagedFileOpener();
  if (file?.url) {
    const id = file.id;
    return (
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={
          id
            ? (e) => {
                if (!isPlainLeftClick(e)) return;
                e.preventDefault();
                void openFile({ id, original_filename: file.original_filename ?? "plik", url: file.url });
              }
            : undefined
        }
        className={clsx(
          "inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline",
          className,
        )}
      >
        {icon}
        {children}
      </a>
    );
  }

  const notice = file?.scan_status ? NOTICES[file.scan_status] : undefined;
  if (notice) {
    const { Icon } = notice;
    return (
      <span
        title={notice.title}
        className={clsx("inline-flex items-center gap-1 text-xs", notice.className, className)}
      >
        <Icon size={iconSize} className={notice.spin ? "animate-spin" : undefined} />
        {notice.label}
      </span>
    );
  }

  return <>{empty}</>;
}
